import { useState, type ChangeEvent, type ReactElement } from 'react';
import {
    CForm,
    CFormInput,
    CFormLabel,
    CFormCheck,
    CFormTextarea,
    CButton,
    CAlert,
    CSpinner,
    CCard,
    CCardBody,
    CCardHeader,
    CRow,
    CCol,
} from '@coreui/react';
import {
    getConfig,
    saveConfig,
    defaultConfig,
    parseConfigJson,
    type AppConfig,
} from '@/services/config.js';
import { getPositions } from '@/services/alpaca.js';
import { getOrders } from '@/services/orders-api.js';

type TestStatus =
    | { kind: 'idle' }
    | { kind: 'success'; message: string }
    | { kind: 'error'; message: string };

const REQUIRED_FIELDS: readonly (keyof AppConfig)[] = [
    'alpacaKeyId',
    'alpacaSecret',
    'primaryApiUrl',
    'apiKey',
    'userId',
];

const hasRequiredFields = (cfg: AppConfig): boolean =>
    REQUIRED_FIELDS.every((field) => cfg[field] !== '');

export const SettingsView = (): ReactElement => {
    const [config, setConfig] = useState<AppConfig>(() => getConfig() ?? defaultConfig());
    const [saved, setSaved] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testStatus, setTestStatus] = useState<TestStatus>({ kind: 'idle' });
    const [importText, setImportText] = useState('');
    const [importError, setImportError] = useState<string | null>(null);

    const updateField =
        (field: keyof AppConfig) =>
        (event: ChangeEvent<HTMLInputElement>): void => {
            setSaved(false);
            setConfig((prev) => ({
                ...prev,
                [field]: field === 'alpacaPaper' ? event.target.checked : event.target.value,
            }));
        };

    const handleSave = (): void => {
        saveConfig(config);
        setSaved(true);
    };

    const handleImportJson = (): void => {
        try {
            setConfig(parseConfigJson(importText));
            setImportError(null);
            setImportText('');
            setSaved(false);
        } catch (error) {
            setImportError((error as Error).message);
        }
    };

    const handleTestConnection = async (): Promise<void> => {
        setTesting(true);
        setTestStatus({ kind: 'idle' });
        try {
            await Promise.all([getPositions(config), getOrders(config)]);
            setTestStatus({
                kind: 'success',
                message: 'Connected to Alpaca and the orders API successfully.',
            });
        } catch (error) {
            setTestStatus({ kind: 'error', message: (error as Error).message });
        } finally {
            setTesting(false);
        }
    };

    const canSave = hasRequiredFields(config);

    return (
        <CCard>
            <CCardHeader>Settings</CCardHeader>
            <CCardBody>
                <CForm>
                    <CRow className="mb-3">
                        <CCol md={12}>
                            <CFormLabel htmlFor="importJson">Import from JSON</CFormLabel>
                            <CFormTextarea
                                id="importJson"
                                rows={3}
                                placeholder='{"alpacaKeyId": "...", "alpacaSecret": "...", "alpacaPaper": true, "primaryApiUrl": "...", "fallbackApiUrl": "", "apiKey": "...", "userId": "..."}'
                                value={importText}
                                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                                    setImportText(event.target.value)
                                }
                            />
                            <div className="form-text">
                                Paste a config JSON string (e.g. generated by{' '}
                                <code>npm run position-app:config</code>) to fill in every field
                                below at once instead of typing them individually.
                            </div>
                            {importError != null && (
                                <CAlert
                                    color="danger"
                                    className="mt-2"
                                    dismissible
                                    onClose={() => setImportError(null)}
                                >
                                    {importError}
                                </CAlert>
                            )}
                            <CButton
                                color="secondary"
                                variant="outline"
                                className="mt-2"
                                disabled={importText.trim() === ''}
                                onClick={handleImportJson}
                            >
                                Load JSON
                            </CButton>
                        </CCol>
                    </CRow>
                    <CRow className="mb-3">
                        <CCol md={6}>
                            <CFormLabel htmlFor="alpacaKeyId">Alpaca Key ID</CFormLabel>
                            <CFormInput
                                id="alpacaKeyId"
                                value={config.alpacaKeyId}
                                onChange={updateField('alpacaKeyId')}
                            />
                        </CCol>
                        <CCol md={6}>
                            <CFormLabel htmlFor="alpacaSecret">Alpaca Secret</CFormLabel>
                            <CFormInput
                                id="alpacaSecret"
                                type="password"
                                value={config.alpacaSecret}
                                onChange={updateField('alpacaSecret')}
                            />
                        </CCol>
                    </CRow>
                    <CRow className="mb-3">
                        <CCol md={6}>
                            <CFormCheck
                                id="alpacaPaper"
                                label="Paper trading"
                                checked={config.alpacaPaper}
                                onChange={updateField('alpacaPaper')}
                            />
                        </CCol>
                    </CRow>
                    <CRow className="mb-3">
                        <CCol md={6}>
                            <CFormLabel htmlFor="primaryApiUrl">Primary API URL</CFormLabel>
                            <CFormInput
                                id="primaryApiUrl"
                                value={config.primaryApiUrl}
                                onChange={updateField('primaryApiUrl')}
                            />
                            <div className="form-text">
                                Base URL of the device orders API (e.g. the trading server's{' '}
                                <code>/api/device</code> prefix). <code>/orders</code> and{' '}
                                <code>/upside</code> are appended automatically.
                            </div>
                        </CCol>
                        <CCol md={6}>
                            <CFormLabel htmlFor="fallbackApiUrl">
                                Fallback API URL (optional)
                            </CFormLabel>
                            <CFormInput
                                id="fallbackApiUrl"
                                value={config.fallbackApiUrl}
                                onChange={updateField('fallbackApiUrl')}
                            />
                            <div className="form-text">
                                Used only if the primary API URL is unreachable. Leave blank to skip
                                the fallback attempt.
                            </div>
                        </CCol>
                    </CRow>
                    <CRow className="mb-3">
                        <CCol md={6}>
                            <CFormLabel htmlFor="apiKey">API Key</CFormLabel>
                            <CFormInput
                                id="apiKey"
                                type="password"
                                value={config.apiKey}
                                onChange={updateField('apiKey')}
                            />
                            <div className="form-text">
                                Sent as the <code>x-api-key</code> header on every request, to both
                                the primary and fallback API.
                            </div>
                        </CCol>
                    </CRow>
                    <CRow className="mb-3">
                        <CCol md={6}>
                            <CFormLabel htmlFor="userId">User ID</CFormLabel>
                            <CFormInput
                                id="userId"
                                value={config.userId}
                                onChange={updateField('userId')}
                            />
                        </CCol>
                    </CRow>

                    {saved && (
                        <CAlert color="success" dismissible onClose={() => setSaved(false)}>
                            Settings saved.
                        </CAlert>
                    )}
                    {testStatus.kind === 'success' && (
                        <CAlert color="success">{testStatus.message}</CAlert>
                    )}
                    {testStatus.kind === 'error' && (
                        <CAlert color="danger">{testStatus.message}</CAlert>
                    )}

                    <div className="d-flex gap-2">
                        <CButton color="primary" disabled={!canSave} onClick={handleSave}>
                            Save
                        </CButton>
                        <CButton
                            color="secondary"
                            variant="outline"
                            disabled={!canSave || testing}
                            onClick={handleTestConnection}
                        >
                            {testing ? <CSpinner size="sm" /> : 'Test connection'}
                        </CButton>
                    </div>
                </CForm>
            </CCardBody>
        </CCard>
    );
};
